import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Megaphone, PushPin, Plus } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export default function Announcements() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", audience: "all", pinned: false });
  const canPost = ["super_admin", "director", "branch_manager", "secretary"].includes(user?.role);

  const load = () => api.get("/announcements").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title || !form.body) return toast.error(t("announcements.toasts.required"));
    try {
      await api.post("/announcements", {
        title: form.title, body: form.body, pinned: form.pinned,
        audience: form.audience === "all" ? ["all"] : [form.audience],
      });
      toast.success(t("announcements.toasts.posted")); setOpen(false);
      setForm({ title: "", body: "", audience: "all", pinned: false }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("nav.announcements")}</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("announcements.title")}</h1>
        </div>
        {canPost && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-announcement-btn" className="rounded-full gap-2"><Plus size={16} weight="bold" /> {t("announcements.newAnnouncement")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("announcements.newAnnouncement")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs uppercase tracking-wider font-semibold">{t("announcements.form.title")}</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-10" /></div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">{t("announcements.form.body")}</Label>
                  <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} className="mt-1.5" /></div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} /> {t("announcements.form.pin")}
                </label>
              </div>
              <DialogFooter><Button onClick={submit} data-testid="announcement-save">{t("announcements.form.post")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {rows.map((a) => (
          <Card key={a.id} className={cn("border-border/60", a.pinned && "border-brand-red")}><CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Megaphone size={18} weight="duotone" />
                </div>
                <div>
                  <h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>{a.title}</h3>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest mt-0.5">
                    {new Date(a.created_at).toLocaleDateString()} · {t("announcements.byAuthor", { author: a.author })}
                  </div>
                </div>
              </div>
              {a.pinned && <Badge variant="destructive" className="gap-1"><PushPin size={10} weight="fill" /> {t("announcements.pinnedBadge")}</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-3 ml-13">{a.body}</p>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}
